"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type YogaDisplayPreviewProps = {
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

/*
 * A sunrise studio: a tall arched window looks out on a pink-gold dawn over
 * hills, and its light falls in a long beam across the oak floor onto a sage
 * mat. A monstera holds the left third; cork blocks, a singing bowl and a
 * smoking incense stick hold the right.
 *
 * One panoramic SVG (640×220, `slice`): a 301px feed card shows the composed
 * centre, a full-width post reveals more of the room. The mat runs under the
 * bottom-centre participant pill. Motion is two compositor-only CSS loops (the
 * beam breathing, the incense smoke rising), off under reduced motion.
 */
const YOGA_KEYFRAMES = `
@keyframes yoga-beam { 0%, 100% { opacity: 0.8 } 50% { opacity: 1 } }
@keyframes yoga-smoke { 0% { transform: translateY(2px); opacity: 0 } 30% { opacity: 0.7 } 100% { transform: translateY(-8px); opacity: 0 } }
.yoga-beam { animation: yoga-beam 6s ease-in-out infinite }
.yoga-smoke { animation: yoga-smoke 4.5s ease-out infinite }
@media (prefers-reduced-motion: reduce) {
  .yoga-beam { animation: none }
  .yoga-smoke { animation: none; opacity: 0.5 }
}
`;

// Floor plank seams converge on a vanishing point above the window.
const FLOOR_TOP = 134;
const VP = { x: 320, y: 40 };
const plankX = (atTop: number, y: number) =>
  VP.x + (atTop - VP.x) * ((y - VP.y) / (FLOOR_TOP - VP.y));

/** One split monstera leaf, drawn pointing up and rotated into place. */
function MonsteraLeaf({
  x,
  y,
  angle,
  size,
  fill,
}: {
  x: number;
  y: number;
  angle: number;
  size: number;
  fill: string;
}) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${angle}) scale(${size})`}>
      <path
        d="M0 0 C-16 -2 -20 -22 -10 -32 C-5 -37 -1 -36 0 -33 C1 -36 5 -37 10 -32 C20 -22 16 -2 0 0 Z"
        fill={fill}
      />
      <path d="M0 0 L0 -33" stroke="#1d4a2c" strokeWidth="0.9" />
      {/* Fenestrations */}
      <path
        d="M-16 -10 L-7 -13 M-17 -19 L-8 -20 M-14 -27 L-6 -26 M16 -10 L7 -13 M17 -19 L8 -20 M14 -27 L6 -26"
        stroke="#eaa987"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M-6 -30 C-2 -34 2 -34 6 -30"
        fill="none"
        stroke="#b9e8a8"
        strokeWidth="0.9"
        opacity="0.7"
      />
    </g>
  );
}

export function YogaDisplayPreview({ participants }: YogaDisplayPreviewProps) {
  const uid = useId().replace(/[^\w-]/g, "");
  const id = (name: string) => `yoga-${uid}-${name}`;
  const url = (name: string) => `url(#${id(name)})`;

  return (
    <ActivityScenePreview
      label="Yoga"
      backgroundClassName="bg-[#d9946e]"
      participants={participants}
      scene={
        <>
          <style href="yoga-scene-keyframes" precedence="default">
            {YOGA_KEYFRAMES}
          </style>
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 640 220"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
            focusable="false"
          >
            <defs>
              <radialGradient id={id("wall")} cx="0.5" cy="0.42" r="0.75">
                <stop offset="0" stopColor="#fbdcc0" />
                <stop offset="0.45" stopColor="#eeb592" />
                <stop offset="1" stopColor="#b9694c" />
              </radialGradient>
              <linearGradient id={id("dawn")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#ffb7a0" />
                <stop offset="0.55" stopColor="#ffd79c" />
                <stop offset="1" stopColor="#fff1c8" />
              </linearGradient>
              <radialGradient id={id("sun")}>
                <stop offset="0" stopColor="#fffdf2" />
                <stop offset="0.25" stopColor="#fff2c2" stopOpacity="0.9" />
                <stop offset="1" stopColor="#ffd79c" stopOpacity="0" />
              </radialGradient>
              <linearGradient id={id("floor")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#b97850" />
                <stop offset="1" stopColor="#7a4428" />
              </linearGradient>
              <linearGradient id={id("beam")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#fff3cf" stopOpacity="0.5" />
                <stop offset="1" stopColor="#fff3cf" stopOpacity="0.08" />
              </linearGradient>
              <linearGradient id={id("mat")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#a9cdb0" />
                <stop offset="1" stopColor="#5e8f6c" />
              </linearGradient>
              <linearGradient id={id("pot")} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="#f6efe4" />
                <stop offset="0.7" stopColor="#e2d6c4" />
                <stop offset="1" stopColor="#a8987f" />
              </linearGradient>
              <linearGradient id={id("cork")} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#e2b77a" />
                <stop offset="1" stopColor="#b5834a" />
              </linearGradient>
              <linearGradient id={id("brass")} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="#7a5418" />
                <stop offset="0.45" stopColor="#f5cf72" />
                <stop offset="1" stopColor="#8a6020" />
              </linearGradient>
            </defs>

            {/* Plaster wall, warmest round the window */}
            <rect width="640" height={FLOOR_TOP} fill={url("wall")} />

            {/* Arched window: dawn over the hills */}
            <path
              d="M256 134 L256 98 A64 64 0 0 1 384 98 L384 134 Z"
              fill="#f6e3cc"
            />
            <path
              d="M263 130 L263 98 A57 57 0 0 1 377 98 L377 130 Z"
              fill={url("dawn")}
            />
            <circle cx="320" cy="108" r="46" fill={url("sun")} />
            <circle cx="320" cy="108" r="10" fill="#fffaf0" />
            <path
              d="M263 116 Q292 104 320 112 T377 108 L377 130 L263 130 Z"
              fill="#c58fa6"
            />
            <path
              d="M263 122 Q300 114 330 120 T377 118 L377 130 L263 130 Z"
              fill="#8e6a8e"
            />
            {/* Mullions */}
            <g stroke="#f6e3cc" strokeWidth="2.4" fill="none">
              <path d="M320 41 L320 130" />
              <path d="M263 98 L377 98" />
              <path d="M276 60 Q320 72 364 60" strokeWidth="1.6" />
            </g>
            {/* Deep reveal + sill */}
            <path
              d="M256 98 A64 64 0 0 1 384 98"
              fill="none"
              stroke="#c98463"
              strokeWidth="1.4"
              opacity="0.6"
            />
            <rect
              x="248"
              y="130"
              width="144"
              height="5"
              rx="1.5"
              fill="#fbefe0"
            />
            <rect
              x="248"
              y="135"
              width="144"
              height="2"
              fill="#8a4c32"
              opacity="0.4"
            />

            {/* Oak floor in perspective */}
            <rect
              y={FLOOR_TOP}
              width="640"
              height={220 - FLOOR_TOP}
              fill={url("floor")}
            />
            {[
              -260, -140, -30, 70, 160, 240, 320, 400, 480, 570, 670, 780, 900,
            ].map((x) => (
              <line
                key={x}
                x1={x}
                y1={FLOOR_TOP}
                x2={plankX(x, 220)}
                y2="220"
                stroke="#4a2414"
                strokeOpacity="0.35"
                strokeWidth="0.8"
              />
            ))}
            {[146, 164, 192].map((y) => (
              <line
                key={y}
                x1="0"
                y1={y}
                x2="640"
                y2={y}
                stroke="#4a2414"
                strokeOpacity="0.12"
                strokeWidth="0.8"
              />
            ))}
            {/* Baseboard */}
            <rect y={FLOOR_TOP - 4} width="640" height="5" fill="#f3dcc4" />
            <rect
              y={FLOOR_TOP + 1}
              width="640"
              height="2"
              fill="#5a2c18"
              opacity="0.35"
            />

            {/* The window's light, thrown across the floor */}
            <g className="yoga-beam">
              <polygon
                points="263,130 377,130 470,220 170,220"
                fill={url("beam")}
              />
              <ellipse
                cx="320"
                cy="190"
                rx="150"
                ry="30"
                fill="#fff0c8"
                opacity="0.16"
              />
            </g>

            {/* Sage mat on the floor, under the participants */}
            <polygon
              points="266,146 374,146 414,222 226,222"
              fill="#3f6a4c"
              opacity="0.5"
            />
            <polygon
              points="268,143 372,143 410,220 230,220"
              fill={url("mat")}
            />
            <line
              x1="268"
              y1="143.6"
              x2="372"
              y2="143.6"
              stroke="#e4f5df"
              strokeWidth="1.2"
              opacity="0.8"
            />
            <line
              x1="320"
              y1="150"
              x2="320"
              y2="220"
              stroke="#e4f5df"
              strokeWidth="0.8"
              opacity="0.35"
            />

            {/* Monstera in a ceramic pot, left third */}
            <g transform="translate(10 0)">
              <ellipse
                cx="206"
                cy="162"
                rx="24"
                ry="4"
                fill="#4a2414"
                opacity="0.4"
              />
              <path
                d="M188 134 L224 134 L220 162 L192 162 Z"
                fill={url("pot")}
              />
              <rect
                x="186"
                y="131"
                width="40"
                height="4.4"
                rx="2"
                fill="#fbf6ee"
              />
              <path
                d="M206 134 Q200 112 190 96 M206 134 Q210 108 222 92 M206 134 Q206 116 204 80 M206 134 Q198 122 186 114 M206 134 Q218 122 234 116"
                stroke="#2e6a3c"
                strokeWidth="1.4"
                fill="none"
              />
              <MonsteraLeaf
                x={186}
                y={114}
                angle={-50}
                size={0.72}
                fill="#2f7a46"
              />
              <MonsteraLeaf
                x={190}
                y={96}
                angle={-28}
                size={0.9}
                fill="#3d8c52"
              />
              <MonsteraLeaf
                x={204}
                y={80}
                angle={-4}
                size={0.95}
                fill="#4a9a5c"
              />
              <MonsteraLeaf
                x={222}
                y={92}
                angle={26}
                size={0.86}
                fill="#3d8c52"
              />
              <MonsteraLeaf
                x={234}
                y={116}
                angle={60}
                size={0.7}
                fill="#2f7a46"
              />
            </g>

            {/* Cork blocks, singing bowl and incense, right third */}
            <g>
              <ellipse
                cx="432"
                cy="166"
                rx="40"
                ry="4"
                fill="#4a2414"
                opacity="0.4"
              />
              {/* Two stacked cork blocks */}
              <rect
                x="400"
                y="148"
                width="34"
                height="18"
                rx="2.5"
                fill={url("cork")}
              />
              <rect
                x="400"
                y="148"
                width="34"
                height="3"
                rx="1.5"
                fill="#f6d39c"
              />
              <rect
                x="404"
                y="132"
                width="28"
                height="16"
                rx="2.5"
                fill={url("cork")}
              />
              <rect
                x="404"
                y="132"
                width="28"
                height="3"
                rx="1.5"
                fill="#f6d39c"
              />
              {[406, 414, 424].map((x) => (
                <circle
                  key={x}
                  cx={x + 2}
                  cy="158"
                  r="0.7"
                  fill="#7a4e22"
                  opacity="0.6"
                />
              ))}
              {/* Singing bowl on its cushion */}
              <ellipse cx="456" cy="164" rx="15" ry="4" fill="#9c3a4a" />
              <path
                d="M442 152 Q442 164 456 164 Q470 164 470 152 Z"
                fill={url("brass")}
              />
              <ellipse cx="456" cy="152" rx="14" ry="2.6" fill="#5a3a10" />
              <ellipse
                cx="456"
                cy="151.6"
                rx="14"
                ry="2.6"
                fill="none"
                stroke="#ffe4a0"
                strokeWidth="0.8"
              />
              {/* Incense in a holder, smoke curling up */}
              <rect
                x="438"
                y="122"
                width="1.4"
                height="26"
                fill="#6b3a1a"
                transform="rotate(12 439 148)"
              />
              <circle cx="433.6" cy="122.6" r="1.2" fill="#ff7a3a" />
              <path
                className="yoga-smoke"
                d="M433 120 C428 112 438 106 432 98 C428 92 434 86 430 80"
                fill="none"
                stroke="#fff8ee"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            </g>

            {/* Trailing pothos on the far walls (wider cards) */}
            {[48, 452, 598].map((x) => (
              <g key={x}>
                <line
                  x1={x}
                  y1="0"
                  x2={x}
                  y2="24"
                  stroke="#d9b48a"
                  strokeWidth="1"
                />
                <path
                  d={`M${x - 12} 24 L${x + 12} 24 L${x + 9} 40 L${x - 9} 40 Z`}
                  fill="#f2e6d6"
                />
                {[0, 1, 2, 3, 4].map((i) => (
                  <ellipse
                    key={i}
                    cx={x - 10 + (i % 2) * 18}
                    cy={46 + i * 9}
                    rx="5"
                    ry="3.4"
                    fill={i % 2 ? "#4a9a5c" : "#2f7a46"}
                  />
                ))}
              </g>
            ))}
          </svg>
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_115%_125%_at_50%_45%,transparent_55%,rgba(74,30,16,0.42)_100%)]" />
      }
    />
  );
}
